/**
 * Admin · AI providers ▸ Usage — WS-HF task 3.
 *
 * The owner's own Higgsfield page, rebuilt in this product's material: a header, a window, four
 * KPI tiles, a share bar with a legend, and the history table under filters and paging.
 *
 * **Two sources, one shape.** On the desktop with an MCP connection the numbers come from the
 * operator's Higgsfield account (`hfMcpBalance` + `hfMcpTransactions`) — the vendor's own
 * figures, including generations run outside this app. Everywhere else they come from this
 * app's log (`GET /api/admin/ai/usage/higgsfield`), which sees only what this app ran but sees
 * a status per job. Neither is a superset of the other, so the page SAYS which one it is
 * showing instead of blending them, and `usageRows.ts` maps both into one `UsageRow` so the
 * columns mean the same thing on both paths.
 *
 * The credit figures are always the source's own. Deriving credits from a dollar cost would
 * read back the operator's own `$ per credit` setting, and every number on the page would move
 * when they edited it.
 *
 * **The company subscription** (WS-HF shared login). Every designer generates on ONE company
 * subscription, so the cloud path also shows that subscription's balance and its own ledger
 * (`subscription`, the vendor's `transactions`, paged by an INTEGER cursor of its own), and a
 * **By designer** table (`by_designer`) — the vendor's ledger cannot tell the designers apart,
 * this app's log can. Both ride the same response as the history, so they cost no extra call.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataTable, type Column } from "../ui/DataTable.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Icon } from "../ui/Icon.js";
import { InfoHint } from "../ui/Tooltip.js";
import { useServices } from "../app/context.js";
import { higgsfieldMcp, type HiggsfieldMcpClient } from "@genz/platform";
import { resolveGenerationSource, thisComputerMayGenerate } from "../stores/generationSource.js";
import {
  SOURCE_CAPTIONS,
  dayAfterIso,
  daysAgoIso,
  distinct,
  kpisFromRows,
  rowsFromCloud,
  rowsFromMcp,
  shareFromRows,
  withinRange,
  type UsageRow,
  type UsageSource,
} from "./usageRows.js";
import type {
  HfMcpBalance,
  HiggsfieldSubscriptionTransaction,
  HiggsfieldUsageByDesigner,
  HiggsfieldUsageShare,
  HiggsfieldUsageSubscription,
} from "@genz/platform";

/** The windows the owner's page offers, plus the custom one. */
const RANGES = [
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "30", label: "Last 30 days", days: 30 },
] as const;
type RangeKey = (typeof RANGES)[number]["key"] | "custom";

const PAGE_SIZES = [25, 50, 100];

/** Rows per page of the company subscription's ledger. */
const TX_SIZE = 20;

/** Money, to the cent. A usage page that prints `$0.1` reads as broken. */
function usd(n: number): string {
  return `$${n.toFixed(2)}`;
}

/** Credits, to at most two places — the vendor bills 1.5 for a Nano Banana. */
function credits(n: number): string {
  return String(Math.round(n * 100) / 100);
}

/** A readable, locale-stable date. Never the raw ISO string, which nobody scans. */
function day(at?: string): string {
  if (!at) return "—";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  return d.toISOString().slice(0, 10);
}

export function UsagePanel({
  /** Injectable for tests; defaults to the process-wide desktop bridge. */
  mcp = higgsfieldMcp(),
  /** The operator's price per credit, from AI settings. */
  usdPerCredit,
}: {
  mcp?: HiggsfieldMcpClient;
  usdPerCredit: number;
}) {
  const { client } = useServices();

  const [source, setSource] = useState<UsageSource | null>(null);
  const [rows, setRows] = useState<UsageRow[]>([]);
  const [balance, setBalance] = useState<HfMcpBalance | null>(null);
  /** The server's own KPIs + share on the cloud path; computed from rows on the MCP path. */
  const [serverShare, setServerShare] = useState<HiggsfieldUsageShare[] | null>(null);
  const [serverKpis, setServerKpis] = useState<{
    totalCostUsd: number; creditsSpent: number; featuresUsed: number; totalGenerations: number;
  } | null>(null);
  const [pages, setPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [range, setRange] = useState<RangeKey>("7");
  const [from, setFrom] = useState(() => daysAgoIso(7));
  const [to, setTo] = useState(() => daysAgoIso(0));
  const [model, setModel] = useState("");
  const [action, setAction] = useState("");
  const [size, setSize] = useState(25);
  const [page, setPage] = useState(1);

  /* The MCP feed is CURSOR-paged: the vendor hands back a `next_cursor`, never a page count.
     The cursor that opened each page is kept so Previous can re-fetch it — a page number alone
     cannot address a cursor feed, and guessing one skips rows. Index 0 is the first page's
     cursor, which is always `undefined`. */
  const cursors = useRef<(string | undefined)[]>([undefined]);
  const [hasNext, setHasNext] = useState(false);

  /* WS-HF shared login — the company subscription and the per-designer totals (cloud path
     only). The ledger is cursor-paged like the MCP feed, but by a NUMBER, and on its own: its
     Previous and Next never move the history table. Index 0 opens with no cursor. */
  const [company, setCompany] = useState<HiggsfieldUsageSubscription | null>(null);
  const [byDesigner, setByDesigner] = useState<HiggsfieldUsageByDesigner[] | null>(null);
  const txCursors = useRef<(number | undefined)[]>([undefined]);
  const [txIndex, setTxIndex] = useState(0);
  const [txLoading, setTxLoading] = useState(false);

  const load = useCallback(async (toPage: number) => {
    setLoading(true);
    setError(null);
    try {
      // The source is where this person's Generate runs: the cloud unless this computer's own
      // sign-in is theirs to use.
      const onDevice = await thisComputerMayGenerate(mcp, client);
      const connected = onDevice ? (await mcp.hfMcpStatus()).connected : false;
      const src: UsageSource = resolveGenerationSource(onDevice, connected) === "higgsfield-mcp"
        ? "higgsfield-mcp"
        : "cloud";
      setSource(src);

      if (src === "higgsfield-mcp") {
        /* The balance is a fact about the ACCOUNT, not about the window, so it is fetched
           beside the feed and shown as its own tile. A failure here must not take the history
           with it: knowing what was spent is useful without knowing what is left. */
        const [bal, feed] = await Promise.all([
          mcp.hfMcpBalance().catch(() => null),
          mcp.hfMcpTransactions(size, cursors.current[toPage - 1]),
        ]);
        setBalance(bal);
        cursors.current[toPage] = feed.nextCursor;
        setHasNext(!!feed.nextCursor);
        setRows(withinRange(rowsFromMcp(feed.items, usdPerCredit), from, to));
        setServerShare(null);
        setServerKpis(null);
        // The designer's desktop account is not the company's: nothing company-wide to show.
        setCompany(null);
        setByDesigner(null);
        setPages(0); // unknown — a cursor feed has no page count to print
      } else {
        const r = await client.higgsfieldUsage({
          // `to` is sent as the EXCLUSIVE next day — see `dayAfterIso`.
          from, to: dayAfterIso(to), page: toPage, size,
          model: model || undefined,
          status: action || undefined,
          // The ledger page on screen stays on screen when the history moves.
          txCursor: txCursors.current[txIndex], txSize: TX_SIZE,
        });
        setBalance(null);
        setCompany(r.subscription ?? null);
        txCursors.current[txIndex + 1] = r.subscription?.transactions?.next_cursor ?? undefined;
        setByDesigner(r.by_designer ?? null);
        setRows(rowsFromCloud(r.items ?? []));
        setServerShare(r.share ?? []);
        setServerKpis({
          totalCostUsd: r.kpis?.total_cost_usd ?? 0,
          creditsSpent: r.kpis?.credits_spent ?? 0,
          featuresUsed: r.kpis?.features_used ?? 0,
          totalGenerations: r.kpis?.total_generations ?? 0,
        });
        setPages(Math.max(1, r.pages ?? 1));
        setHasNext(toPage < Math.max(1, r.pages ?? 1));
      }
      setPage(toPage);
    } catch (e) {
      // Never a silent empty table: an empty window and an unreachable source look identical
      // on screen, and one of them means the operator should stop trusting the tiles.
      setError(e instanceof Error ? e.message : String(e));
      // …and nothing from the PREVIOUS query stays on screen under the error: a spend figure
      // for a window that is no longer the one asked for is a wrong number, not a stale one.
      setRows([]);
      setServerKpis(null);
      setServerShare(null);
      setBalance(null);
      setCompany(null);
      setByDesigner(null);
    } finally {
      setLoading(false);
    }
  }, [client, mcp, from, to, model, action, size, usdPerCredit, txIndex]);

  /** One page of the company ledger. Only the subscription block is taken from the answer. */
  const loadTx = useCallback(async (toIndex: number) => {
    setTxLoading(true);
    setError(null);
    try {
      const r = await client.higgsfieldUsage({
        from, to: dayAfterIso(to), page, size,
        model: model || undefined,
        status: action || undefined,
        txCursor: txCursors.current[toIndex], txSize: TX_SIZE,
      });
      setCompany(r.subscription ?? null);
      txCursors.current[toIndex + 1] = r.subscription?.transactions?.next_cursor ?? undefined;
      setTxIndex(toIndex);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setTxLoading(false);
    }
  }, [client, from, to, page, size, model, action]);

  // Any change to the window, the filters or the page size starts again at page 1 — page 4 of
  // a different query is a page that was never asked for.
  useEffect(() => {
    cursors.current = [undefined];
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, model, action, size]);

  function chooseRange(key: RangeKey) {
    setRange(key);
    const preset = RANGES.find((r) => r.key === key);
    if (preset) {
      setFrom(daysAgoIso(preset.days));
      setTo(daysAgoIso(0));
    }
  }

  /* The MCP feed is filtered HERE because the vendor's endpoint takes neither filter. The
     cloud path sends both as query parameters instead, so its rows arrive already filtered and
     running them through this again would only be able to remove rows the server kept. */
  const shown = useMemo(() => {
    if (source !== "higgsfield-mcp") return rows;
    return rows.filter((r) => (!model || r.feature === model) && (!action || r.action === action));
  }, [rows, source, model, action]);

  const kpis = serverKpis ?? kpisFromRows(shown, usdPerCredit);
  const share = serverShare ?? shareFromRows(shown);
  /** The tiles' scope on the vendor feed, which is one cursor page — see the tiles below. */
  const pageScope = source === "higgsfield-mcp" ? " · this page" : "";
  const models = distinct(rows, (r) => r.feature);
  const actions = distinct(rows, (r) => r.action);

  const columns: Column<UsageRow>[] = [
    {
      key: "credits", header: "Credits", numeric: true,
      // A spend is `−N`, a top-up `+N`, and a row that cost NOTHING — a failed or refused job —
      // is `0`: a `+0` said money came in.
      render: (r) => (r.isSpend ? `−${credits(r.credits)}` : r.credits === 0 ? "0" : `+${credits(r.credits)}`),
    },
    { key: "feature", header: "Feature", render: (r) => r.feature },
    { key: "action", header: "Action", render: (r) => r.action || "—" },
    { key: "at", header: "Date", render: (r) => day(r.at) },
  ];

  const designerColumns: Column<HiggsfieldUsageByDesigner>[] = [
    // A deleted account has no email left; the row stays, because its spend happened.
    { key: "designer", header: "Designer", render: (d) => d.designer ?? "Unknown account" },
    { key: "jobs", header: "Jobs", numeric: true, render: (d) => String(d.generations) },
    { key: "credits", header: "Credits", numeric: true, render: (d) => credits(d.credits) },
  ];

  const txColumns: Column<HiggsfieldSubscriptionTransaction>[] = [
    {
      key: "credits", header: "Credits", numeric: true,
      // The vendor's own sign: a spend is negative. Same voice as the history table.
      render: (t) => (t.credits < 0 ? `−${credits(-t.credits)}` : t.credits === 0 ? "0" : `+${credits(t.credits)}`),
    },
    { key: "what", header: "What", render: (t) => t.display_name || "—" },
    { key: "action", header: "Action", render: (t) => t.action || "—" },
    { key: "at", header: "Date", render: (t) => day(t.created_at ?? undefined) },
  ];

  return (
    <div className="usage" data-testid="providers-usage-panel">
      <div className="admin-panel__head admin-panel__head--flush">
        <div>
          <strong className="admin-panel__title panel-title">
            Usage history{" "}
            <InfoHint text="Credits and cost for the selected window. The source is named under this heading — the two accounts do not see the same generations." />
          </strong>
          <p className="admin-panel__sub">View credits usage, history and statistics</p>
          <p className="admin-panel__sub" data-testid="usage-source">
            {source ? SOURCE_CAPTIONS[source] : "Reading…"}
          </p>
        </div>
      </div>

      <div className="usage__toolbar">
        <div className="seg admin-seg" role="group" aria-label="Date range">
          {[...RANGES, { key: "custom" as const, label: "Custom" }].map((r) => (
            <button
              key={r.key}
              type="button"
              className={`seg__btn${range === r.key ? " seg__btn--on" : ""}`}
              aria-pressed={range === r.key}
              onClick={() => chooseRange(r.key)}
              data-testid={`usage-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {range === "custom" && (
          <span className="usage__dates">
            <label className="sr-only" htmlFor="usage-from">From</label>
            <input
              id="usage-from" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="usage-from"
            />
            <span className="faint">→</span>
            <label className="sr-only" htmlFor="usage-to">To</label>
            <input
              id="usage-to" type="date" value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="usage-to"
            />
          </span>
        )}

        <span className="usage__spacer" />

        <button
          type="button"
          className="btn btn--sm"
          disabled={loading}
          onClick={() => { cursors.current = [undefined]; txCursors.current = [undefined]; setTxIndex(0); void load(1); }}
          data-testid="usage-refresh"
        >
          <Icon name="refresh" size={16} /> Refresh
        </button>
      </div>

      {error && <p className="banner banner--danger" role="alert" data-testid="usage-error">{error}</p>}

      <div className="kpi-grid usage__kpis" data-testid="usage-kpis">
        {balance && (
          /* Only the Higgsfield account knows what is LEFT. The cloud log records spending and
             has no view of the operator's balance, so this tile appears on that path only —
             rather than printing a zero that reads as an empty account. */
          <div className="kpi-card" data-testid="usage-kpi-balance">
            <span className="kpi-card__value">{credits(balance.credits)}</span>
            <span className="kpi-card__label">Balance{balance.plan ? ` · ${balance.plan}` : ""}</span>
          </div>
        )}
        {company?.connected && (
          /* The COMPANY subscription's balance — what is left for every designer. A fact about
             the account, not about the window, like the desktop account's tile above. */
          <div className="kpi-card" data-testid="usage-company-balance">
            <span className="kpi-card__value">{company.credits != null ? credits(company.credits) : "—"}</span>
            <span className="kpi-card__label">Company balance{company.plan ? ` · ${company.plan}` : ""}</span>
          </div>
        )}
        {/* On the cursor-paged vendor feed the tiles are computed from the rows on screen, and
            they SAY so: a whole-window total is a number one page cannot know, and a tile that
            looked like the cloud path's would be read as one. */}
        <div className="kpi-card" data-testid="usage-kpi-cost">
          <span className="kpi-card__value">{usd(kpis.totalCostUsd)}</span>
          <span className="kpi-card__label">Total cost{pageScope}</span>
        </div>
        <div className="kpi-card" data-testid="usage-kpi-credits">
          <span className="kpi-card__value">{credits(kpis.creditsSpent)}</span>
          <span className="kpi-card__label">Credits spent{pageScope}</span>
        </div>
        <div className="kpi-card" data-testid="usage-kpi-features">
          <span className="kpi-card__value">{kpis.featuresUsed}</span>
          <span className="kpi-card__label">Features used{pageScope}</span>
        </div>
        <div className="kpi-card" data-testid="usage-kpi-generations">
          <span className="kpi-card__value">{kpis.totalGenerations}</span>
          <span className="kpi-card__label">Total generations{pageScope}</span>
        </div>
      </div>

      {share.length > 0 && (
        <div className="usage__share" data-testid="usage-share">
          {/* The bar is decoration for the legend beside it, which carries the same numbers as
              text — so it is `aria-hidden` rather than a chart a screen reader must narrate. */}
          <div className="usage__bar" aria-hidden="true">
            {share.map((s, i) => (
              <span
                key={s.model}
                className="usage__bar-seg"
                data-tone={String((i % 6) + 1)}
                style={{ width: `${s.pct}%` }}
              />
            ))}
          </div>
          <ul className="usage__legend">
            {share.map((s, i) => (
              <li key={s.model} className="usage__legend-item" data-testid="usage-legend-item">
                <span className="usage__swatch" data-tone={String((i % 6) + 1)} aria-hidden="true" />
                <span className="usage__legend-name">{s.model}</span>
                <span className="usage__legend-pct">{s.pct.toFixed(1)}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="usage__toolbar">
        <label className="sr-only" htmlFor="usage-filter-model">Model</label>
        <select
          id="usage-filter-model"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          data-testid="usage-filter-model"
        >
          <option value="">All models</option>
          {models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>

        <label className="sr-only" htmlFor="usage-filter-action">Action</label>
        <select
          id="usage-filter-action"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          data-testid="usage-filter-action"
        >
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>

        <span className="usage__spacer" />

        <label className="sr-only" htmlFor="usage-page-size">Rows per page</label>
        <select
          id="usage-page-size"
          value={String(size)}
          onChange={(e) => setSize(Number(e.target.value))}
          data-testid="usage-page-size"
        >
          {PAGE_SIZES.map((n) => <option key={n} value={String(n)}>{n} per page</option>)}
        </select>
      </div>

      {loading ? (
        <p className="faint" data-testid="usage-loading">Loading usage…</p>
      ) : (
        <DataTable
          columns={columns}
          rows={shown}
          rowKey={(r) => r.id}
          testId="usage-table"
          caption="Higgsfield usage history"
          rowProps={() => ({ "data-testid": "usage-row" })}
          empty={
            <EmptyState
              icon="list"
              title="No usage in this window"
              hint="Widen the date range, or clear the filters."
              testId="usage-empty"
            />
          }
        />
      )}

      <div className="usage__pager" data-testid="usage-pager">
        <button
          type="button"
          className="btn btn--sm"
          disabled={loading || page <= 1}
          onClick={() => void load(page - 1)}
          data-testid="usage-prev"
        >
          Previous
        </button>
        <span className="faint" data-testid="usage-page-label">
          {/* A cursor feed has no page COUNT, so the page says what it knows and no more.
              Printing "Page 2 of 2" from a `next_cursor` would be an invented total. */}
          {pages > 0 ? `Page ${page} of ${pages}` : `Page ${page}`}
        </span>
        <button
          type="button"
          className="btn btn--sm"
          disabled={loading || !hasNext}
          onClick={() => void load(page + 1)}
          data-testid="usage-next"
        >
          Next
        </button>
      </div>

      {byDesigner && (
        <section className="usage__section" data-testid="usage-by-designer-section">
          <h3 className="admin-section-title">
            By designer{" "}
            <InfoHint text="Jobs and credits per designer over the selected window. Every designer shares one company subscription, so only this app's log can tell them apart." />
          </h3>
          <DataTable
            columns={designerColumns}
            rows={byDesigner}
            rowKey={(r) => r.customer_id ?? r.designer ?? "unknown"}
            testId="usage-by-designer"
            caption="Higgsfield usage by designer"
            rowProps={() => ({ "data-testid": "usage-designer-row" })}
            empty={<EmptyState icon="list" title="No designer ran a job in this window" testId="usage-by-designer-empty" />}
          />
        </section>
      )}

      {company && (
        <section className="usage__section" data-testid="usage-company">
          <h3 className="admin-section-title">
            Company subscription{" "}
            <InfoHint text="The company Higgsfield subscription's own ledger, as Higgsfield keeps it. It includes anything run on that account outside this app." />
          </h3>
          {!company.connected ? (
            <p className="caption">The company subscription is not connected. Connect it in the Settings view of this tab.</p>
          ) : (
            <>
              {company.error && <p className="caption" data-testid="usage-company-note">{company.error}</p>}
              {company.transactions && (
                <>
                  <DataTable
                    columns={txColumns}
                    rows={company.transactions.items ?? []}
                    rowKey={(t) => `${t.created_at ?? "?"}-${t.display_name ?? ""}-${t.credits}`}
                    testId="usage-company-tx"
                    caption="Company subscription transactions"
                    rowProps={() => ({ "data-testid": "usage-company-tx-row" })}
                    empty={<EmptyState icon="list" title="No transactions on this page" testId="usage-company-tx-empty" />}
                  />
                  <div className="usage__pager">
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={txLoading || txIndex === 0}
                      onClick={() => void loadTx(txIndex - 1)}
                      data-testid="usage-company-prev"
                    >
                      Previous
                    </button>
                    <span className="faint" data-testid="usage-company-page">Page {txIndex + 1}</span>
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={txLoading || txCursors.current[txIndex + 1] === undefined}
                      onClick={() => void loadTx(txIndex + 1)}
                      data-testid="usage-company-next"
                    >
                      Next
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
